import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysDataExport } from './queryKeys';
import {
  type DataExportCreateRequest,
  type DataExportJobCreated,
} from './types';
import {
  dataExportCreateRequestSchema,
  dataExportJobCreatedSchema,
} from './validations';

/**
 * Create and enqueue a data export job.
 *
 * @param {DataExportCreateRequest} payload - Export job creation request
 * @returns {Promise<DataExportJobCreated>} The response containing jobId and requestId
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Export/operation/create_export_job_data_export_create_post
 */
export const createExportJob = async (
  payload: DataExportCreateRequest,
): Promise<DataExportJobCreated> => {
  const response = await vestigasApi
    .post(ENDPOINT.DATA_EXPORT.CREATE(), {
      json: payload,
      validate: { body: dataExportCreateRequestSchema },
    })
    .json();

  return dataExportJobCreatedSchema.parse(response);
};

/**
 * React Query mutation hook for creating a data export job.
 * Automatically invalidates the export results query on success.
 *
 * @param options - Additional mutation options
 */
export const useMutationCreateExportJob = (
  options?: Omit<
    UseMutationOptions<DataExportJobCreated, Error, DataExportCreateRequest>,
    'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    ...options,
    mutationFn: createExportJob,
    async onSuccess() {
      // Invalidate export results to refetch
      await queryClient.invalidateQueries({
        queryKey: [queryKeysDataExport.base()],
      });
    },
  });

  return mutation;
};
